/**
 * What every dashboard widget card shares. Ported from the prototype's
 * `views/cards/cardShell.ts`; the rows-based `cardState` became a status-based
 * one, because a card no longer sees rows — it sees a status and its data.
 */
import type { ReactNode } from 'react';
import { Button } from '../../../atoms/Button/Button';
import type { CardHeaderDragHandle } from '../../../molecules/CardHeader/CardHeader';
import { TARGET_TICKS } from '../../../molecules/ChartFrame/chartTheme';
import styles from './moduleGrid.module.css';

/** The plot height every md card draws at (Figma `Chart` slot). */
export const PLOT_HEIGHT = 224;

/** The placeholder for a figure that cannot be computed. */
export const NONE = '—';

export const pct = (value: number) => `${value.toFixed(1)}%`;
export const pts = (value: number) => `${value >= 0 ? '+' : ''}${value.toFixed(1)} pts`;

/**
 * Where a widget's data stands. `error` and `notConnected` have no Figma
 * frame yet (docs/design/open-questions.md #2) — they render as the card's
 * empty body with an explanatory line until the designer draws them.
 */
export type WidgetStatus = 'loading' | 'ready' | 'error' | 'notConnected';

export type CardStateProps = {
  state: 'default' | 'loading' | 'empty';
  emptyLabel?: ReactNode;
};

/**
 * The prototype's rule, status-aware: loading wins over everything; a card is
 * empty unless it has rows **and** its chart has something to draw (the same
 * predicate the chart uses, so the two never disagree).
 */
export function cardState(status: WidgetStatus, hasData: boolean, onRetry?: () => void): CardStateProps {
  if (status === 'loading') return { state: 'loading' };
  if (status === 'notConnected') {
    return { state: 'empty', emptyLabel: 'Live data for this widget is not connected yet.' };
  }
  if (status === 'error') {
    return {
      state: 'empty',
      emptyLabel: (
        <span className={styles.stateMessage}>
          Couldn’t load this widget.
          {onRetry && (
            <Button hierarchy="ghostColor" onClick={onRetry}>
              Try again
            </Button>
          )}
        </span>
      ),
    };
  }
  return hasData ? { state: 'default' } : { state: 'empty' };
}

/** A card's width in the 12-column module grid. */
export type CardSpan = 3 | 4 | 5 | 6 | 7 | 8 | 12;

/**
 * Card widths at the 1440 frame (1192px content, 20px gutters), from which
 * the tick budget is read. Measured in Figma; do not recompute.
 */
const SPAN_WIDTH: Readonly<Record<CardSpan, number>> = {
  3: 283,
  4: 384,
  5: 485,
  6: 586,
  7: 687,
  8: 788,
  12: 1192,
};

export function spanClass(span: CardSpan | undefined): string | undefined {
  switch (span) {
    case 3:
      return styles.span3;
    case 4:
      return styles.span4;
    case 5:
      return styles.span5;
    case 7:
      return styles.span7;
    case 8:
      return styles.twoThirds;
    case 12:
      return styles.wide;
    default:
      return undefined;
  }
}

/** ~10 labels on a wide time axis, fewer on narrow cards (docs/design/rules.md §3). */
export function ticksFor(span: CardSpan | undefined): number {
  if (span === undefined) return TARGET_TICKS;
  if (SPAN_WIDTH[span] <= SPAN_WIDTH[4]) return 5;
  if (SPAN_WIDTH[span] <= SPAN_WIDTH[5]) return 7;
  return TARGET_TICKS;
}

/** Report-widget chrome a card forwards to `Card` (drag handle, remove, flash). */
export type CardChrome = {
  dragHandle?: CardHeaderDragHandle;
  onRemove?: () => void;
  removeLabel?: string;
  dragging?: boolean;
  flash?: number;
};

/** Props every widget block takes besides its data. */
export type WidgetBlockProps = {
  status: WidgetStatus;
  span?: CardSpan;
  chrome?: CardChrome;
  onRetry?: () => void;
};
